import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MainLayout from "../layouts/MainLayout";
import useApi from "../utils/useApi";
import { useLoading } from "../context/LoadingContext";
import BasicInfoBox from "../components/BasicInfoBox"; // ✅ Reusing from CreateClients
import UserSettings from "../components/UserSettings"; // ✅ Reusing from CreateClients
import Toaster, { ToastContainerComponent } from "../components/Toaster";
import { Modal } from "bootstrap";
import { useAuth } from "../context/AuthContext";

const EditClient = () => {
  const { id } = useParams();
  const { callApi } = useApi();
  const { setLoading } = useLoading();
  const navigate = useNavigate();
  const [errors] = useState({});
  const { user } = useAuth();



  const [searchFolderQuery, setSearchFolderQuery] = useState("");
  const [allFolders, setAllFolders] = useState([]);
  const [tempSelectedFolders, setTempSelectedFolders] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [oldSelectedFolders, setoldSelectedFolders] = useState([]);

  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderDetails, setNewFolderDetails] = useState("");
  const [assignClientToFolder, setAssignClientToFolder] = useState("yes");



  const [basicInfoList, setBasicInfoList] = useState([
    { id: id, user_name: "", user_email: "", user_password: "", user_company: "" }
  ]);

  const [userSettings, setUserSettings] = useState({
    changePassword: false,
    accessSettings: false,
  });



  const fetchClient = async () => {
    setLoading(true);
    try {
      const response = await callApi({ endpoint: `/admin/clients/show/${id}` });
      if (response?.success) {
        setBasicInfoList([{ ...response.data, id }]);
        setUserSettings({
          changePassword: response.data.change_password || false,
          accessSettings: response.data.access_user_settings || false,
        });
        getAssignedFolder(id);
      } else {
        Toaster.error("Failed to load client data.");
      }
    } catch (error) {
      console.error("Error fetching client:", error);
      Toaster.error("Error fetching client details.");
    } finally {
      setLoading(false);
    }
  };

  const getAssignedFolder = async (user_id) => {
    setLoading(true);
    try {
      const response = await callApi({
        endpoint: "/admin/directories/get-directory-by-asigned-user",
        method: "POST",
        data: {
          user_id: user_id
        },
      });
      if (response?.success) {
        setSelectedFolders(response.data || []);
        setoldSelectedFolders(response.data || []);
      } else {
        Toaster.error("Failed to load client data.");
      }
    } catch (error) {
      console.error("Error fetching client:", error);
      Toaster.error("Error fetching client details.");
    } finally {
      setLoading(false);
    }
  }

  // ✅ Fetch client data when page loads
  useEffect(() => {

    fetchClient();
  }, [id]);

  // ✅ Handle Input Change for Each Field
  const handleInputChange = (id, field, value) => {
    if (field === "user_name") {
      // const isValid = /^[a-zA-Z\s]*$/.test(value); // allows only letters and spaces
      if (!/^[a-zA-Z\s]*$/.test(value)) {
        Toaster.error("Name should contain only alphabets.");
        return; // stop further update
      }
    }
    // console.log(field,'testing....edit')
    setBasicInfoList((prevList) =>
      prevList.map((info) => (info.id === id ? { ...info, [field]: value } : info))
    );
  };

  // ✅ Handle User Settings Change
  const handleUserSettingsChange = (field, value) => {
    setUserSettings({ ...userSettings, [field]: value });
  };



  // ✅ Handle Form Submission (Update Client)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const requestData = {
      user_name: basicInfoList[0].user_name,
      user_email: basicInfoList[0].user_email,
      user_company: basicInfoList[0].user_company,
      user_role: "CLIENT",
      user_type: "CLIENT",
      access_user_settings: userSettings.accessSettings,
      change_password: userSettings.changePassword,
      user_password: basicInfoList[0].user_password,
    };

    try {
      const response = await callApi({
        endpoint: `/admin/clients/update/${id}`,
        method: "PUT",
        data: requestData,
      });

      if (response?.success) {
        Toaster.success("Client updated successfully!");
        console.log(selectedFolders);
        //here i want to check which folders are not in new selectedFolders those ids
        const removedFolders = oldSelectedFolders.filter(
          oldFolder => !selectedFolders.some(folder => folder.entity_id === oldFolder.entity_id)
        );
        if (removedFolders?.length > 0) {
        removeFolderTouser(removedFolders.map(folder => folder.entity_id), response?.data?.user_id);
        }
        if (selectedFolders.length > 0) {

          assignFolderTouser(selectedFolders.map(folder => folder.entity_id), response?.data?.user_id);
        }
        setTimeout(() => navigate("/browse-clients"), 1500);
      } else {
        Toaster.error(response?.data?.user_email);
      }
    } catch (error) {
      console.error("Error updating client:", error);
      Toaster.error(error?.response?.data?.data?.user_email?.message ?? error?.response?.data?.data?.user_email);
    } finally {
      setLoading(false);
    }
  };

  const openFolderModal = () => {
    setTempSelectedFolders([...selectedFolders]); // Copy current selections to temp

    const modalElement = document.getElementById("addFolderModal");
    const modalInstance = Modal.getInstance(modalElement) || new Modal(modalElement);
    modalInstance.show();
  };

  const openAddFolderModal = () => {

    const modalElement = document.getElementById("createFolder");
    const modalInstance = Modal.getInstance(modalElement) || new Modal(modalElement);
    modalInstance.show();
  };

  const fetchFolders = async (keyword) => {
    if (keyword.length < 3) {
      setAllFolders([]); // Clear list if query is less than 3 characters
      return;
    }

    setIsSearching(true);
    try {
      const response = await callApi({
        endpoint: "/admin/directories/get-sub-directory",
        params: { keyword }, // Pass keyword for search
      });

      if (response?.success) {
        setAllFolders(response?.data?.directories || []);
      } else {
        Toaster.error("Failed to load directories.");
      }
    } catch (error) {
      console.error("Error fetching directories:", error);
      Toaster.error("Error fetching directories.");
    } finally {
      setIsSearching(false);
    }
  };

  const toggleTempFolderSelection = (folder) => {
    setTempSelectedFolders((prevFolders) => {
      return prevFolders.some((f) => f.entity_id === folder.entity_id)
        ? prevFolders.filter((f) => f.entity_id !== folder.entity_id) // Remove user
        : [...prevFolders, folder]; // Add user
    });
  };

  // ✅ Assign Users from Modal to Main List
  const handleAssignFolders = () => {
    setSelectedFolders(tempSelectedFolders); // Update assigned users
    // Close modal
    const modalElement = document.getElementById("addFolderModal");
    const modalInstance = Modal.getInstance(modalElement);
    modalInstance.hide();
  };

  // ✅ Remove Assigned User
  const removeAssignedFolder = (entityId) => {
    setSelectedFolders((prevFolders) => {
      const updatedUsers = prevFolders.filter((f) => f.entity_id !== entityId);
      return updatedUsers;
    });
  };

  const assignFolderTouser = async (entity_ids, userid) => {
    setLoading(true);
    try {
      const data = {
        directory_ids: entity_ids,
        user_ids: [userid]
      };
      const response = await callApi({
        endpoint: "/admin/directories/add-directory-asigned-user",
        method: "POST",
        data: data,
      });

      //console.log("response", response.data.data);

      if (response?.success) {
        Toaster.success("Users assigned to folder");
      } else {
        Toaster.error("Failed to assigned to folder."); // ✅ Using Toaster for errors
      }
    } catch (error) {
      console.error("Error assigned to folder:", error);
      Toaster.error("Error assigned to folder. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const removeFolderTouser = async (entity_ids, userid) => {
    setLoading(true);
    try {
      const data = {
        entity_ids: entity_ids,
        user_ids: [userid]
      };
      const response = await callApi({
        endpoint: "/admin/directories/remove-directory-asigned-user",
        method: "POST",
        data: data,
      });

      //console.log("response", response.data.data);

      if (response?.success) {
        console.log('deleetd')
      } else {
        console.log('failed to deleted')
      }
    } catch (error) {
      console.error("Error assigned to folder:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle Folder Creation
  const createFolder = async () => {
    if (!newFolderName.trim()) {
      Toaster.error("Folder name is required."); // ✅ Toaster instead of alert
      return;
    }

    setLoading(true);
    try {
      const response = await callApi({
        endpoint: "/admin/directories/create-directory",
        method: "POST",
        data: {
          entity_name: newFolderName,
          entity_description: newFolderDetails,
          parent_directory_id: "",
          directory_owner: user.user_id
        },
      });

      if (response?.success) {

        console.log(response.data);


        if (assignClientToFolder === "yes") {
          if (response.data && typeof response.data === 'object') {
            setSelectedFolders(prevFolders => [...prevFolders, response.data]);
          } else {
            console.warn("Unexpected response format:", response.data);
          }
        }
        setNewFolderName("");
        setNewFolderDetails("");
        setAssignClientToFolder("yes");
        Toaster.success("Folder created successfully!");

        // ✅ Close modal properly
        const modalElement = document.getElementById("createFolder");
        const modalInstance = Modal.getInstance(modalElement) || new Modal(modalElement);
        modalInstance.hide();

        // ✅ Remove lingering backdrop & blur effect
        document.body.classList.remove("modal-open");
        document.querySelectorAll(".modal-backdrop").forEach((backdrop) => backdrop.remove());
      } else {
        Toaster.error(response?.data?.message || "Failed to create folder.");
      }
    } catch (error) {
      console.error("Error creating folder:", error);
      Toaster.error("Error creating folder. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const sendClientCredentials = async () => {
    if (!id) {
      Toaster.error("User ID is missing!");
      return;
    }

    setLoading(true);
    try {
      const response = await callApi({
        endpoint: "/admin/clients/send-credential",
        method: "POST",
        data: {
          user_id: id,  // The client ID from useParams()
        },
      });

      if (response?.success) {
        Toaster.success("Login credentials sent successfully!");
      } else {
        Toaster.error(response?.data?.message || "Failed to send credentials.");
      }
    } catch (error) {
      console.error("Error sending client credentials:", error);
      Toaster.error("Error sending credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <MainLayout>
      <ToastContainerComponent /> {/* ✅ Toast Notifications */}
      <div className="inner_full_right_part">
        <div className="row">
          <div className="col-md-12">
            <div className="global_cardbox">
              <div className="global_card_header">
                <div className="global_head_tag">
                  <span><i className="fa-solid fa-user-pen"></i></span> Edit Client
                </div>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="create_new_client_holder">
                  <div className="global_cardbox inner_global_card">
                    <div className="global_card_header d-flex">
                      <div className="global_head_tag"><span>1</span> Basic Info</div>
                    </div>
                    <div className="create_new_client_holder">
                      {basicInfoList.map((info) => (
                        <BasicInfoBox
                          key={info.id}
                          id={info.id}
                          userData={info}
                          onDelete={() => { }} // ❌ No Delete Button for Edit
                          onInputChange={handleInputChange}
                          isDeletable={false} // ❌ Can't delete the single user being edited
                          errorMessage={errors[info.user_email]} // ✅ Show validation errors
                        />
                      ))}
                    </div>
                  </div>

                  {/* ✅ User Settings Section */}
                  <UserSettings userSettings={userSettings} onSettingsChange={handleUserSettingsChange} />

                  {/* ✅ Restore Folder Access Section */}
                  <div className="global_cardbox inner_global_card">
                    <div className="global_card_header">
                      <div className="global_head_tag"><span>3</span> Give User Access To Folders <b>(Recommended)</b></div>
                    </div>
                    <div className="user_access_permission">
                      <div className="row">
                        <div className="col-12 col-md-4">
                          <div className="add_user_box">
                            <a href="#!" onClick={openFolderModal}>
                              <i className="fas fa-folder-open"></i>
                            </a>
                            <div className="assigne_fldr">Assign Folders</div>
                          </div>
                        </div>
                        <div className="col-12 col-md-4">
                          <div className="add_user_box">
                            <a href="#!" onClick={openAddFolderModal}><i className="fa-solid fa-user-plus"></i></a>
                            <div className="assigne_fldr">Add Folder</div>
                          </div>
                        </div>
                      </div>
                      {selectedFolders.length > 0 && (
                        <div className="col-12 assigned-users mt-2">
                          <strong>Assigned Folders:</strong>
                          <ul>
                            {selectedFolders.map((folder) => (
                              <li key={folder.entity_id}>
                                {folder.entity_name}
                                <button onClick={() => removeAssignedFolder(folder.entity_id)}>✖</button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* <div className="global_cardbox inner_global_card">
                    <div className="global_card_header">
                      <div className="global_head_tag"><span>4</span> Send Client Credentials</div>
                    </div>
                    <div className="user_access_permission">
                      <div className="row">
                        <div className="col-12">
                          <a href="#! " onClick={sendClientCredentials}>Mail login credential to client</a>

                        </div>
                      </div>
                    </div>
                  </div> */}

                  <div className="update_btn_holder">
                    <button type="submit" className="btn btn-primary">Update</button>
                    <button type="button" className="btn btn-primary orange_btn" onClick={() => navigate("/browse-clients")}>
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Add People Modal */}
      <div className="modal fade global_modal addpeople_modal" id="addFolderModal" tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content ">
            <div className="modal-header">
              <h5 className="modal-title">Select Folders</h5>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="folderName">Search Folder</label>
                <input type="text" placeholder="Search Folders..." value={searchFolderQuery} onChange={(e) => {
                  setSearchFolderQuery(e.target.value);
                  fetchFolders(e.target.value);
                }}
                />
                {isSearching && <p>Loading folders...</p>}
              </div>


              {/* User Selection */}
              <ul className="search_ppl">
                {allFolders.map((folder) => (
                  <li key={folder.entity_id}>
                    <input type="checkbox" checked={tempSelectedFolders.some((f) => f.entity_id === folder.entity_id)} onChange={() => toggleTempFolderSelection(folder)} />
                    {folder.entity_name}
                  </li>
                ))}
              </ul>

              {/* ✅ Assign Button */}
              <button onClick={handleAssignFolders} className="btn btn-primary">Assign</button>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Create Folder Modal */}
      <div className="modal fade global_modal" tabIndex="-1" id="createFolder">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Create Folder</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <div className="global_modal_body">
                <div className="form-group">
                  <label htmlFor="folderName">Name</label>
                  <input
                    type="text"
                    id="folderName"
                    className="form-control"
                    placeholder="Folder Name"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="folderDetails">Details</label>
                  <textarea
                    id="folderDetails"
                    placeholder="Details"
                    className="form-control"
                    maxLength="1000"
                    value={newFolderDetails}
                    onChange={(e) => setNewFolderDetails(e.target.value)}
                  />
                  <p>Character Limit: 1000</p>
                </div>

                {/* ✅ Add People to Folder */}
                <div className="form-group">
                  <label>Assign current client to this folder? </label>
                  <div className="chk_holder">
                    <div className="form-check">

                      <input className="form-check-input" type="radio" name="assignFolder" value="yes" checked={assignClientToFolder === "" || assignClientToFolder === "yes"} onChange={() => setAssignClientToFolder("yes")} />
                      <label className="form-check-label" htmlFor="assignFolder">
                        Yes
                      </label>
                    </div>
                    <div className="form-check">
                      <input className="form-check-input" type="radio" name="assignFolder" value="yes" onChange={() => setAssignClientToFolder("no")} />
                      <label className="form-check-label" htmlFor="assignFolder">
                        No
                      </label>
                    </div>
                  </div>
                  <span className="note">* Select "Yes" to assign users, otherwise, the folder will be personal.</span>
                </div>

                {/* ✅ Buttons */}
                <div className="btn_holder">
                  <button type="button" className="btn btn-primary" onClick={createFolder}>
                    Submit
                  </button>
                  <button data-bs-dismiss="modal" className="btn btn-primary orange_btn">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EditClient;
